import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import {
  Box,
  Dialog,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { useTheme } from "@mui/material/styles";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import RowElement from "components/RowElement";
import SnackbarMessage from "components/SnackbarMessage";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import AccreditationChain from "pages/LicensureBodiesPage/AccreditationChain";
import QualificationApprovalsList from "pages/QualificationsPage/QualificationApprovalsList";
import QualificationForm from "pages/QualificationsPage/QualificationForm";
import QualificationStatementList from "pages/QualificationsPage/QualificationStatementList";
import QualificationStudyFieldList from "pages/QualificationsPage/QualificationStudyFieldList";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ResponseOfQualification } from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { SnackbarMessageProps } from "types/common-types";

const useStyles = makeStyles((theme: Theme) => ({
  tableTitle: {
    margin: "20px 0",
  },
}));

const QualificationDetails = () => {
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { registerPromise } = useTrashablePromises();
  const navigate = useNavigate();
  const styles = useStyles(useTheme());
  const { id } = useParams<{ id: string }>();
  const {
    qualificationService,
    qualificationsUrl,
    qualificationSkusUrl,
  } = useGlobalContext();
  const [updateOpen, updateOpenActions] = useBoolean(false);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const { organizationId } = contextStore.useState();

  const qualificationResult = useStrictQuery<ResponseOfQualification>(
    ["qualification", organizationId, id],
    () => qualificationService.getQualificationById(id!),
    {
      enabled: !!id,
    },
  );

  const accreditationChainResult = useStrictQuery(
    ["qualificationService.getAccreditationChain", organizationId, id],
    () => qualificationService.getAccreditationChain(id!),
    {
      enabled: !!id,
    },
  );

  const qualification = qualificationResult.data?.data;
  const hasOrgOverride = !!qualification?.organizationQualificationId;
  useTitle(qualification?.value || "Accreditation Body Details");

  const onQualificationUpdated = () => {
    queryClient.invalidateQueries(["qualification", organizationId]);
    queryClient.invalidateQueries(["qualificationService.getQualifications"]);

    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "accreditationBodyUpdated" })),
    );

    updateOpenActions.setFalse();
  };

  const handleDelete = async () => {
    await registerPromise(qualificationService.deleteQualification(id!));
    queryClient.invalidateQueries(["qualificationService.getQualifications"]);
    queryClient.invalidateQueries(["qualification", organizationId]);
    navigate(qualificationsUrl());
  };

  const rows = [
    RowElement(
      "valueLabel",
      qualification?.value!,
      qualificationResult.isLoading,
    ),
    RowElement(
      "acronymLabel",
      qualification?.acronym!,
      qualificationResult.isLoading,
    ),
    RowElement(
      "accreditationBodyFormDisplayValueLabel",
      qualification?.displayValue ?? "-",
      qualificationResult.isLoading,
      <span
        dangerouslySetInnerHTML={{ __html: qualification?.displayValue ?? "-" }}
      ></span>,
    ),
    RowElement(
      "displayOrderLabel",
      qualification?.displayOrder!,
      qualificationResult.isLoading,
    ),
    RowElement(
      "qualificationType",
      qualification?.qualificationTypeName ?? "-",
      qualificationResult.isLoading,
    ),
  ].filter((r) => !!r.component);

  if (!id) return <Navigate to={qualificationsUrl()} replace />;
  if (qualificationResult.isLoading) return <Loader />;
  if (!qualificationResult.isLoading && !qualification) {
    return (
      <>
        <RedirectBackButton
          redirectRoute={qualificationsUrl()}
          labelKey="backToListButtonLabel"
        />
        <Alert severity="info">
          {t({ id: "accreditationBodyNotAccessible" })}
        </Alert>
      </>
    );
  }

  return (
    <>
      <RedirectBackButton
        redirectRoute={qualificationsUrl()}
        labelKey="backToListButtonLabel"
      />
      <h1>{qualification?.value}</h1>
      <Box mb={3} display="flex" justifyContent="center" gap={4}>
        <Tooltip
          title={
            hasOrgOverride
              ? t({ id: "accreditationBodyFormEditTitle" })
              : t({ id: "accreditationBodyInherited" })
          }
        >
          <Box>
            <Fab
              color="primary"
              onClick={updateOpenActions.setTrue}
              disabled={!hasOrgOverride}
            >
              <EditIcon />
            </Fab>
          </Box>
        </Tooltip>
        <Tooltip title={t({ id: "associatedSkus" })}>
          <Fab
            color="primary"
            onClick={() => navigate(qualificationSkusUrl(id))}
          >
            <LocalLibraryIcon />
          </Fab>
        </Tooltip>
        <Tooltip
          title={
            hasOrgOverride
              ? t({ id: "accreditationBodyDeleteTitle" })
              : t({ id: "accreditationBodyInherited" })
          }
        >
          <Box>
            <Fab
              color="primary"
              onClick={deleteOpenActions.setTrue}
              disabled={!hasOrgOverride}
            >
              <DeleteIcon />
            </Fab>
          </Box>
        </Tooltip>
      </Box>
      <Typography variant="h5" sx={styles.tableTitle}>
        {t({ id: "accreditationBodyLabel" })}
      </Typography>
      <TableContainer>
        <Table>
          <TableBody>
            {rows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell component="th" scope="row">
                  <b>{title}</b>
                </TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <QualificationStatementList qualification={qualification!} />
      <QualificationApprovalsList qualification={qualification!} />
      <QualificationStudyFieldList qualificationId={qualification?.id!} />

      <AccreditationChain items={accreditationChainResult.data?.data} />

      <Dialog open={updateOpen} disableEnforceFocus>
        <QualificationForm
          qualification={qualification}
          title={t({ id: "accreditationBodyFormEditTitle" })}
          onSubmit={onQualificationUpdated}
          onCancel={updateOpenActions.setFalse}
        />
      </Dialog>

      <DeleteEntityDialog
        dialogTitleKey="accreditationBodyDeleteTitle"
        contentKey="accreditationBodyDeleteQuestion"
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default QualificationDetails;
